import React, { useEffect, useRef, useState } from "react";
import { X, Download, ExternalLink, Pencil, RefreshCcw, Trash2, StickyNote, Loader2, AlertTriangle } from "lucide-react";
import { useFilePreview } from "@/context/FilePreviewContext";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { STATUS_META } from "@/pages/Compliance";

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select, [tabindex]:not([tabindex="-1"])';

function computeStatus(expiresAt) {
  if (!expiresAt) return "valid";
  const d = new Date(`${expiresAt}T00:00:00`);
  if (isNaN(d)) return "valid";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "valid";
}

function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return s;
  return d.toLocaleDateString();
}

/**
 * Slide-over preview for a driver document or maintenance receipt, mounted once by
 * FilePreviewProvider's host (Layout.jsx for the agency console, DriverPortal.jsx for
 * drivers). Renders nothing until openPreview() is called via useFilePreview().
 */
export default function FilePreviewDrawer() {
  const { isOpen, kind, id, page, meta, status, blobUrl, contentType, closePreview, retry, openPreview, updateMeta } = useFilePreview();

  const panelRef = useRef(null);
  const closeBtnRef = useRef(null);
  const lastFocusedRef = useRef(null);

  const [entered, setEntered] = useState(false);
  const [editingExpiry, setEditingExpiry] = useState(false);
  const [expiryDraft, setExpiryDraft] = useState("");
  const [savingExpiry, setSavingExpiry] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const replaceInputRef = useRef(null);

  // Slide-in on open (reduced-motion users skip straight to the open position via motion-safe:).
  useEffect(() => {
    if (!isOpen) { setEntered(false); return; }
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [isOpen]);

  // Reset the per-document edit UI whenever a different file is opened.
  useEffect(() => {
    setEditingExpiry(false);
    setExpiryDraft(meta?.expires_at || "");
    setNoteDraft("");
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Focus trap, Esc-to-close, and focus restore on close.
  useEffect(() => {
    if (!isOpen) return undefined;
    lastFocusedRef.current = document.activeElement;
    const focusTimer = setTimeout(() => closeBtnRef.current?.focus(), 0);
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closePreview();
        return;
      }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusables = Array.from(panelRef.current.querySelectorAll(FOCUSABLE));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      clearTimeout(focusTimer);
      const el = lastFocusedRef.current;
      if (el && typeof el.focus === "function") el.focus();
    };
  }, [isOpen, closePreview]);

  if (!isOpen) return null;

  const isDocument = kind === "document";
  const canEdit = !!meta?.canEdit;
  const isImage = (contentType || "").startsWith("image/");
  const isPdf = (contentType || "").includes("pdf");
  const token = localStorage.getItem("nemt_token");
  const fileUrl = isDocument
    ? `/drivers/documents/${id}/file`
    : kind === "page"
      ? `/drivers/documents/${id}/pages/${page}/file`
      : kind === "agreement"
        ? `/agency/agreements/${id}/pdf`
        : `/drivers/maintenance/${id}/file`;
  const newTabUrl = `${API_BASE}${fileUrl}?auth=${encodeURIComponent(token || "")}`;
  const statusKey = meta?.status || (isDocument ? computeStatus(meta?.expires_at) : null);
  const statusInfo = statusKey ? STATUS_META[statusKey] : null;
  const notes = meta?.notes || [];

  const notifyChanged = () => meta?.onChanged?.();

  const downloadFile = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = meta?.original_filename || "file";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const saveExpiry = async () => {
    setSavingExpiry(true);
    try {
      const r = await api.patch(`/drivers/documents/${id}`, { expires_at: expiryDraft || null });
      updateMeta({ expires_at: r.data.expires_at, status: computeStatus(r.data.expires_at) });
      setEditingExpiry(false);
      toast.success("Expiry saved");
      notifyChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingExpiry(false);
    }
  };

  const clearExpiry = async () => {
    setSavingExpiry(true);
    try {
      const r = await api.patch(`/drivers/documents/${id}`, { expires_at: null });
      updateMeta({ expires_at: r.data.expires_at, status: "valid" });
      setExpiryDraft("");
      setEditingExpiry(false);
      toast.success("Expiry cleared");
      notifyChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingExpiry(false);
    }
  };

  const saveNote = async () => {
    const text = noteDraft.trim();
    if (!text) return toast.error("Write a note first");
    setSavingNote(true);
    try {
      const r = await api.patch(`/drivers/documents/${id}`, { note: text });
      updateMeta({ notes: r.data.notes });
      setNoteDraft("");
      toast.success("Note saved");
      notifyChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSavingNote(false);
    }
  };

  const replaceFile = async (file) => {
    if (!file) return;
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("doc_type", meta.doc_type);
      if (meta.expires_at) fd.append("expires_at", meta.expires_at);
      fd.append("replaces", id);
      fd.append("file", file);
      const r = await api.post(`/drivers/${meta.driver_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File replaced");
      notifyChanged();
      openPreview({
        kind: "document",
        id: r.data.id,
        meta: {
          ...meta,
          expires_at: r.data.expires_at,
          uploaded_at: r.data.uploaded_at,
          original_filename: r.data.original_filename,
          content_type: r.data.content_type,
          notes: r.data.notes,
          status: computeStatus(r.data.expires_at),
        },
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setReplacing(false);
    }
  };

  const removeFile = async () => {
    if (!window.confirm("Delete this file? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete(isDocument ? `/drivers/documents/${id}` : `/drivers/maintenance/${id}`);
      toast.success("Deleted");
      notifyChanged();
      closePreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-[70] motion-safe:transition-opacity motion-safe:duration-150"
        style={{ opacity: entered ? 1 : 0 }}
        onClick={closePreview}
        data-testid="filepreview-backdrop"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={meta?.label ? `Preview: ${meta.label}` : "File preview"}
        className={`fixed right-0 top-0 h-full z-[71] bg-white shadow-2xl flex flex-col w-full sm:w-[min(560px,100vw)]
          motion-safe:transition-transform motion-safe:ease-out
          ${entered ? "translate-x-0" : "translate-x-full"}`}
        style={{ transitionDuration: "160ms" }}
        data-testid="filepreview-drawer"
      >
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-display font-semibold text-slate-900 truncate">{meta?.label || "File"}</div>
            {meta?.driver_name && <div className="text-xs text-slate-500 mt-0.5 truncate">{meta.driver_name}</div>}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-500">
              {statusInfo && (
                <span className={`inline-flex items-center gap-1 font-medium rounded-full px-2 py-0.5 border ${statusInfo.cls}`}>
                  <statusInfo.Icon size={10} /> {statusInfo.label}
                </span>
              )}
              {meta?.expires_at && <span>Expires {meta.expires_at}</span>}
              {meta?.uploaded_at && <span>Uploaded {fmtDate(meta.uploaded_at)}</span>}
              {meta?.original_filename && <span className="font-mono truncate max-w-[200px]">{meta.original_filename}</span>}
            </div>
          </div>
          <button
            ref={closeBtnRef}
            onClick={closePreview}
            className="h-11 w-11 flex-shrink-0 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-50"
            aria-label="Close preview"
            data-testid="filepreview-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2" data-testid="filepreview-tools">
          <ToolButton icon={Download} label="Download" onClick={downloadFile} disabled={!blobUrl} testId="tool-download" />
          <a
            href={newTabUrl}
            target="_blank"
            rel="noreferrer"
            className="tool-btn min-h-[44px] px-3 inline-flex items-center gap-1.5 text-xs rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50"
            data-testid="tool-newtab"
          >
            <ExternalLink size={13} /> Open in new tab
          </a>
          {canEdit && isDocument && (
            <>
              <ToolButton icon={Pencil} label="Edit expiry" onClick={() => setEditingExpiry((v) => !v)} testId="tool-edit-expiry" />
              <ToolButton
                icon={RefreshCcw}
                label="Replace file"
                busy={replacing}
                onClick={() => replaceInputRef.current?.click()}
                testId="tool-replace"
              />
              <input
                ref={replaceInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (file) replaceFile(file);
                }}
                data-testid="filepreview-replace-input"
              />
              <ToolButton
                icon={StickyNote}
                label="Add note"
                onClick={() => document.getElementById("filepreview-note-box")?.focus()}
                testId="tool-add-note"
              />
            </>
          )}
          {canEdit && (
            <ToolButton icon={Trash2} label="Delete" onClick={removeFile} busy={deleting} tone="danger" testId="tool-delete" />
          )}
        </div>

        {canEdit && isDocument && editingExpiry && (
          <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2" data-testid="filepreview-expiry-editor">
            <input
              type="date"
              value={expiryDraft}
              onChange={(e) => setExpiryDraft(e.target.value)}
              className="min-h-[44px] text-sm px-2 py-1.5 rounded-md border border-slate-200"
              data-testid="filepreview-expiry-input"
            />
            <button onClick={saveExpiry} disabled={savingExpiry} className="btn-primary min-h-[44px] text-xs px-3 disabled:opacity-60" data-testid="filepreview-save-expiry">
              Save expiry
            </button>
            <button onClick={clearExpiry} disabled={savingExpiry} className="min-h-[44px] text-xs px-3 rounded-md border border-slate-200 disabled:opacity-60" data-testid="filepreview-clear-expiry">
              Clear expiry
            </button>
          </div>
        )}

        <div className="flex-1 overflow-auto bg-slate-50 flex items-center justify-center p-3" data-testid="filepreview-body">
          {status === "loading" && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Loading…
            </div>
          )}
          {status === "error" && (
            <div className="text-sm text-center space-y-3">
              <div className="text-rose-600 flex items-center gap-2 justify-center">
                <AlertTriangle size={16} /> Could not load this file.
              </div>
              <div className="flex items-center justify-center gap-2">
                <button onClick={retry} className="min-h-[44px] text-xs px-3 rounded-md border border-slate-200" data-testid="filepreview-retry">
                  Retry
                </button>
                <a href={newTabUrl} target="_blank" rel="noreferrer" className="min-h-[44px] inline-flex items-center text-xs px-3 rounded-md btn-primary" data-testid="filepreview-fallback-link">
                  Open in new tab
                </a>
              </div>
            </div>
          )}
          {status === "ready" && blobUrl && isImage && (
            <img src={blobUrl} alt={meta?.original_filename || "Document preview"} className="max-w-full max-h-full object-contain" />
          )}
          {status === "ready" && blobUrl && isPdf && (
            <iframe title={meta?.original_filename || "Document preview"} src={blobUrl} className="w-full h-full border-0 bg-white" />
          )}
          {status === "ready" && blobUrl && !isImage && !isPdf && (
            <div className="text-sm text-slate-500 text-center space-y-3">
              <div>Preview not available for this file type.</div>
              <a href={newTabUrl} target="_blank" rel="noreferrer" className="min-h-[44px] inline-flex items-center text-xs px-3 rounded-md btn-primary">
                Open in new tab
              </a>
            </div>
          )}
        </div>

        {isDocument && (
          <div className="border-t border-slate-200 px-5 py-3 max-h-[240px] overflow-y-auto" data-testid="filepreview-notes">
            {canEdit && (
              <div className="flex items-start gap-2 mb-3">
                <textarea
                  id="filepreview-note-box"
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  rows={2}
                  placeholder="Add a note for this document..."
                  className="flex-1 text-sm px-2 py-1.5 rounded-md border border-slate-200"
                  data-testid="filepreview-note-input"
                />
                <button onClick={saveNote} disabled={savingNote || !noteDraft.trim()} className="btn-primary min-h-[44px] text-xs px-3 disabled:opacity-60" data-testid="filepreview-save-note">
                  Save note
                </button>
              </div>
            )}
            {notes.length === 0 && <div className="text-xs text-slate-400">No notes yet.</div>}
            {[...notes].reverse().map((n) => (
              <div key={n.id} className="text-xs text-slate-600 border-b border-slate-100 py-2 last:border-0">
                <div>{n.text}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{n.by_name} · {fmtDate(n.at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ToolButton({ icon: Icon, label, onClick, disabled, busy, tone = "default", testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || busy}
      className={`inline-flex items-center gap-1.5 min-h-[44px] px-3 text-xs rounded-md border disabled:opacity-50 ${
        tone === "danger" ? "border-rose-200 text-rose-600 hover:bg-rose-50" : "border-slate-200 text-slate-700 hover:bg-slate-50"
      }`}
      data-testid={testId}
    >
      <Icon size={13} /> {busy ? "Working…" : label}
    </button>
  );
}
